import { normalizePlotSpec, parameterByLegacyId, plotByLegacyId } from './registry';
import { clampTimestamp, normalizedRange, parseEpoch } from './time';
import type { AnalysisDataMode, AnalysisMode, AnalysisState, ComparisonMode, PlotKind, PlotSpec, WorkspaceState } from './types';

interface LegacyCompactWorkspace {
  v: 2;
  g: string;
  r: [number, number];
  c: number;
  p: Array<[
    string,
    PlotKind,
    string,
    string | null,
    0 | 1,
    [number, number] | null,
    number | null,
    Record<string, 0 | 1>,
    [string, number] | null,
    string[] | null
  ]>;
}

type PackedPlot = [number, number, number, ...unknown[]];
type PackedWorkspace = [string, number, number, number, PackedPlot[]];

// The URL codec version is independent of WorkspaceState.version.
const WORKSPACE_CODEC_PREFIX = '3.';
const ANALYSIS_CODEC_PREFIX = '1.';
const MAX_WORKSPACE_TOKEN_LENGTH = 65_536;
const MAX_ANALYSIS_TOKEN_LENGTH = 4_096;
export const MAX_WORKSPACE_PLOTS = 32;
export const DEFAULT_COMPARISON_MODE: ComparisonMode = 'same-location';
const DEFAULT_ANALYSIS_WINDOW = 24;
const FALLBACK_RANGE: [number, number] = [Date.UTC(2013, 0, 1) / 1000, Date.UTC(2015, 0, 1) / 1000];

// These wire tags are permanent. Add new values; never reorder or reuse tags.
const PLOT_KIND_BY_TAG: Readonly<Record<number, PlotKind>> = {
  0: 'average-series',
  1: 'phase-series',
  2: 'day-polar',
  3: 'heatmap',
  4: 'harmonic-spectrum',
  5: 'waveform',
  6: 'harmonic-trend'
};
const PLOT_TAG_BY_KIND = Object.fromEntries(
  Object.entries(PLOT_KIND_BY_TAG).map(([tag, kind]) => [kind, Number(tag)])
) as Readonly<Record<PlotKind, number>>;

const ANALYSIS_MODE_BY_TAG: Readonly<Record<number, AnalysisMode>> = {
  0: 'rolling-correlation',
  1: 'normalized-comparison',
  2: 'raw-comparison',
  3: 'difference-from-baseline',
  4: 'rms-peak-envelope',
  5: 'frequency-spectrum',
  6: 'calculated-power',
  7: 'three-phase-balance',
  8: 'time-frequency',
  9: 'event-ensemble'
};
const ANALYSIS_TAG_BY_MODE = Object.fromEntries(
  Object.entries(ANALYSIS_MODE_BY_TAG).map(([tag, mode]) => [mode, Number(tag)])
) as Readonly<Record<AnalysisMode, number>>;

const PARAMETER_ID_BY_TAG: Readonly<Record<number, string>> = {
  0: 'current',
  1: 'voltage-ln',
  2: 'frequency',
  3: 'apparent-power',
  4: 'real-power',
  5: 'reactive-power',
  6: 'power-factor',
  7: 'thd',
  8: 'tdd',
  9: 'negative-sequence-voltage',
  10: 'zero-sequence-voltage',
  11: 'negative-sequence-current',
  12: 'zero-sequence-current',
  13: 'flicker-instantaneous',
  14: 'flicker-short',
  15: 'flicker-long'
};
const PARAMETER_TAG_BY_ID = Object.fromEntries(
  Object.entries(PARAMETER_ID_BY_TAG).map(([tag, id]) => [id, Number(tag)])
) as Readonly<Record<string, number>>;

// Default state costs zero bits: linked time, visible fundamental, other options off.
const PLOT_FLAG = {
  unlinkedTime: 1,
  hideFundamental: 2,
  showEvents: 4,
  showHistogram: 8,
  cumulative: 16,
  showNeutral: 32,
  perUnit: 64,
  showInterharmonics: 128
} as const;

// Each extra bit owns one JSON value, emitted in ascending order. New fields
// must use a new highest bit so older v3 readers can ignore the trailing value.
const PLOT_EXTRA = {
  location: 1,
  range: 2,
  cursor: 4,
  selectedEvent: 8,
  hiddenClassifications: 16
} as const;

let fallbackId = 0;

export function createPlotId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  fallbackId += 1;
  return `plot-${Date.now().toString(36)}-${fallbackId.toString(36)}`;
}

export function createPlotSpec(
  kind: PlotKind = 'average-series',
  parameterId = 'current',
  id = createPlotId()
): PlotSpec {
  return normalizePlotSpec({
    id,
    kind,
    parameterId,
    location: { mode: 'global' },
    linkedTime: true,
    options: {
      showEvents: false,
      showHistogram: false,
      cumulative: false,
      showNeutral: false,
      showFundamental: true,
      perUnit: false,
      showInterharmonics: false
    }
  });
}

export function createDefaultWorkspace(
  monitorId: string,
  range: [number, number],
  cursor = range[0]
): WorkspaceState {
  const safeRange = normalizedRange(range[0], range[1], FALLBACK_RANGE);
  return {
    version: 2,
    globalMonitorId: monitorId,
    range: safeRange,
    cursor: clampTimestamp(cursor, safeRange),
    plots: [createPlotSpec()]
  };
}

export function createDefaultAnalysisState(): AnalysisState {
  return {
    open: false,
    dataMode: 'plot-data',
    selectedPlotIds: [],
    mode: 'raw-comparison',
    windowSize: DEFAULT_ANALYSIS_WINDOW,
    seriesByPlot: {},
    waveformSeriesByPlot: {}
  };
}

function encodeBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function analysisSeriesKey(plotId: string, seriesId: string): number | string | null {
  const prefix = `${plotId}:`;
  if (!seriesId.startsWith(prefix)) return null;
  const suffix = seriesId.slice(prefix.length);
  const index = Number(suffix);
  if (Number.isSafeInteger(index) && index >= 0 && String(index) === suffix) return index;
  return /^w:\d+$/.test(suffix) ? suffix : null;
}

function encodedSeriesSelections(
  selections: Record<string, string>,
  plotIndex: Map<string, number>
): Array<[number, number | string]> {
  return Object.entries(selections).flatMap(([id, seriesId]) => {
    const panelIndex = plotIndex.get(id);
    const seriesKey = analysisSeriesKey(id, seriesId);
    return panelIndex == null || seriesKey == null ? [] : [[panelIndex, seriesKey]];
  });
}

function decodedSeriesSelections(value: unknown, workspace: WorkspaceState): Record<string, string> {
  const result: Record<string, string> = {};
  if (!Array.isArray(value)) return result;
  for (const entry of value) {
    if (!Array.isArray(entry) || entry.length !== 2 || !safeInteger(entry[0])) continue;
    const seriesKey = safeInteger(entry[1]) && entry[1] >= 0
      ? String(entry[1])
      : typeof entry[1] === 'string' && /^(?:w:\d+|\d+)$/.test(entry[1]) ? entry[1] : null;
    const plot = workspace.plots[entry[0]];
    if (plot && seriesKey != null) result[plot.id] = `${plot.id}:${seriesKey}`;
  }
  return result;
}

function encodeAnalysisState(analysis: AnalysisState, workspace: WorkspaceState): string | null {
  const plotIndex = new Map(workspace.plots.map((plot, index) => [plot.id, index]));
  const selected = Array.from(new Set(analysis.selectedPlotIds))
    .flatMap((id) => plotIndex.has(id) ? [plotIndex.get(id)!] : [])
    .slice(0, 4);
  const series = encodedSeriesSelections(analysis.seriesByPlot, plotIndex);
  const waveformSeries = encodedSeriesSelections(analysis.waveformSeriesByPlot, plotIndex);
  const windowSize = Math.max(3, Math.min(500, Math.round(analysis.windowSize)));
  const isDefault = !analysis.open
    && analysis.dataMode === 'plot-data'
    && selected.length === 0
    && analysis.mode === 'raw-comparison'
    && windowSize === DEFAULT_ANALYSIS_WINDOW
    && series.length === 0
    && waveformSeries.length === 0;
  if (isDefault) return null;
  return `${ANALYSIS_CODEC_PREFIX}${encodeBase64Url(JSON.stringify([
    analysis.open ? 1 : 0,
    ANALYSIS_TAG_BY_MODE[analysis.mode] ?? 0,
    windowSize,
    selected,
    series,
    analysis.dataMode === 'event-waveforms' ? 1 : 0,
    waveformSeries
  ]))}`;
}

export function parseAnalysisState(params: URLSearchParams, workspace: WorkspaceState): AnalysisState {
  const fallback = createDefaultAnalysisState();
  const token = params.get('a') ?? params.get('analysis');
  if (!token || token.length > MAX_ANALYSIS_TOKEN_LENGTH || !token.startsWith(ANALYSIS_CODEC_PREFIX)) return fallback;
  try {
    const decoded = JSON.parse(decodeBase64Url(token.slice(ANALYSIS_CODEC_PREFIX.length))) as unknown;
    if (!Array.isArray(decoded) || decoded.length < 5) return fallback;
    const mode = safeInteger(decoded[1]) ? ANALYSIS_MODE_BY_TAG[decoded[1]] : undefined;
    const windowSize = safeInteger(decoded[2]) ? Math.max(3, Math.min(500, decoded[2])) : DEFAULT_ANALYSIS_WINDOW;
    const selectedPlotIds = Array.isArray(decoded[3])
      ? Array.from(new Set(decoded[3].flatMap((index) => safeInteger(index) && workspace.plots[index] ? [workspace.plots[index].id] : []))).slice(0, 4)
      : [];
    const seriesByPlot = decodedSeriesSelections(decoded[4], workspace);
    const dataMode: AnalysisDataMode = decoded[5] === 1 ? 'event-waveforms' : 'plot-data';
    const waveformSeriesByPlot = decodedSeriesSelections(decoded[6], workspace);
    return {
      open: decoded[0] === 1,
      dataMode,
      selectedPlotIds,
      mode: mode ?? fallback.mode,
      windowSize,
      seriesByPlot,
      waveformSeriesByPlot
    };
  } catch {
    return fallback;
  }
}

function hasBit(value: number, bit: number): boolean {
  return Math.floor(value / bit) % 2 === 1;
}

function monitorTag(monitorId: string, globalMonitorId: string): 0 | string {
  return monitorId === globalMonitorId ? 0 : monitorId;
}

function monitorFromTag(value: unknown, globalMonitorId: string): string | null {
  if (value === 0) return globalMonitorId;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function classificationTag(key: string): string {
  if (key.startsWith('cause:')) return `c/${key.slice(6)}`;
  if (key.startsWith('status:')) return `s/${key.slice(7)}`;
  return key;
}

function classificationFromTag(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (value.startsWith('c/')) return `cause:${value.slice(2)}`;
  if (value.startsWith('s/')) return `status:${value.slice(2)}`;
  return value;
}

function plotFlags(plot: PlotSpec): number {
  let flags = 0;
  if (!plot.linkedTime) flags += PLOT_FLAG.unlinkedTime;
  if (plot.options.showFundamental === false) flags += PLOT_FLAG.hideFundamental;
  if (plot.options.showEvents) flags += PLOT_FLAG.showEvents;
  if (plot.options.showHistogram) flags += PLOT_FLAG.showHistogram;
  if (plot.options.cumulative) flags += PLOT_FLAG.cumulative;
  if (plot.options.showNeutral) flags += PLOT_FLAG.showNeutral;
  if (plot.options.perUnit) flags += PLOT_FLAG.perUnit;
  if (plot.options.showInterharmonics) flags += PLOT_FLAG.showInterharmonics;
  return flags;
}

function requiredWireTag(tag: number | undefined, label: string): number {
  if (tag == null) throw new Error(`Workspace URL tag is not defined for ${label}.`);
  return tag;
}

function packedPlot(plot: PlotSpec, workspace: WorkspaceState): PackedPlot {
  const start = workspace.range[0];
  const entry: PackedPlot = [
    requiredWireTag(PLOT_TAG_BY_KIND[plot.kind], `plot kind ${plot.kind}`),
    requiredWireTag(PARAMETER_TAG_BY_ID[plot.parameterId], `parameter ${plot.parameterId}`),
    plotFlags(plot)
  ];
  let extras = 0;
  const values: unknown[] = [];

  if (plot.location.mode === 'override') {
    extras += PLOT_EXTRA.location;
    values.push(monitorTag(plot.location.monitorId, workspace.globalMonitorId));
  }
  if (plot.rangeOverride) {
    extras += PLOT_EXTRA.range;
    values.push([plot.rangeOverride[0] - start, plot.rangeOverride[1] - plot.rangeOverride[0]]);
  }
  if (plot.cursorOverride != null) {
    extras += PLOT_EXTRA.cursor;
    values.push(plot.cursorOverride - start);
  }
  if (plot.selectedEvent) {
    extras += PLOT_EXTRA.selectedEvent;
    values.push([
      monitorTag(plot.selectedEvent.monitorId, workspace.globalMonitorId),
      plot.selectedEvent.timestamp - start
    ]);
  }
  if (plot.hiddenEventClassifications?.length) {
    extras += PLOT_EXTRA.hiddenClassifications;
    values.push(plot.hiddenEventClassifications.map(classificationTag));
  }
  if (extras) entry.push(extras, ...values);
  return entry;
}

function compact(workspace: WorkspaceState): PackedWorkspace {
  const start = workspace.range[0];
  return [
    workspace.globalMonitorId,
    start,
    workspace.range[1] - start,
    workspace.cursor - start,
    workspace.plots.map((plot) => packedPlot(plot, workspace))
  ];
}

export function encodeWorkspace(workspace: WorkspaceState): string {
  if (workspace.plots.length > MAX_WORKSPACE_PLOTS) {
    throw new Error(`A workspace URL can contain at most ${MAX_WORKSPACE_PLOTS} plots.`);
  }
  const encoded = `${WORKSPACE_CODEC_PREFIX}${encodeBase64Url(JSON.stringify(compact(workspace)))}`;
  if (encoded.length > MAX_WORKSPACE_TOKEN_LENGTH) {
    throw new Error('The workspace is too large to store safely in a URL.');
  }
  return encoded;
}

function isKind(value: unknown): value is PlotKind {
  return [
    'average-series', 'phase-series', 'day-polar', 'heatmap',
    'harmonic-spectrum', 'waveform', 'harmonic-trend'
  ].includes(String(value));
}

function decodeLegacyWorkspace(value: string): WorkspaceState | null {
  try {
    const decoded = JSON.parse(decodeBase64Url(value)) as Partial<LegacyCompactWorkspace>;
    if (decoded.v !== 2 || typeof decoded.g !== 'string' || !Array.isArray(decoded.r) || decoded.r.length !== 2 || !Array.isArray(decoded.p) || decoded.p.length > MAX_WORKSPACE_PLOTS) return null;
    const range = normalizedRange(Number(decoded.r[0]), Number(decoded.r[1]), FALLBACK_RANGE);
    const decodedPlots = decoded.p.flatMap((entry) => {
      if (!Array.isArray(entry) || typeof entry[0] !== 'string' || !isKind(entry[1]) || typeof entry[2] !== 'string') return [];
      const options = entry[7] && typeof entry[7] === 'object'
        ? Object.fromEntries(Object.entries(entry[7]).map(([key, enabled]) => [key, enabled === 1]))
        : {};
      const rangeOverride = Array.isArray(entry[5]) && entry[5].length === 2
        ? normalizedRange(Number(entry[5][0]), Number(entry[5][1]), range)
        : undefined;
      return [normalizePlotSpec({
        id: entry[0] || createPlotId(),
        kind: entry[1],
        parameterId: entry[2],
        location: typeof entry[3] === 'string' && entry[3] ? { mode: 'override', monitorId: entry[3] } : { mode: 'global' },
        linkedTime: entry[4] !== 0,
        rangeOverride,
        cursorOverride: typeof entry[6] === 'number' ? clampTimestamp(entry[6], rangeOverride ?? range) : undefined,
        selectedEvent: Array.isArray(entry[8])
          && typeof entry[8][0] === 'string'
          && entry[8][0].length > 0
          && Number.isSafeInteger(entry[8][1])
          && entry[8][1] >= 0
          ? { monitorId: entry[8][0], timestamp: entry[8][1] }
          : undefined,
        hiddenEventClassifications: Array.isArray(entry[9])
          ? entry[9].filter((key): key is string => typeof key === 'string')
          : undefined,
        options
      })];
    });
    const ids = new Set<string>();
    const plots = decodedPlots.map((plot) => {
      if (!ids.has(plot.id)) {
        ids.add(plot.id);
        return plot;
      }
      const id = createPlotId();
      ids.add(id);
      return { ...plot, id };
    });
    return {
      version: 2,
      globalMonitorId: decoded.g,
      range,
      cursor: clampTimestamp(Number(decoded.c), range),
      plots: decoded.p.length > 0 && plots.length === 0 ? [createPlotSpec()] : plots
    };
  } catch {
    return null;
  }
}

function safeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

function offsetValue(start: number, offset: unknown): number | null {
  if (!safeInteger(offset)) return null;
  const value = start + offset;
  return Number.isSafeInteger(value) ? value : null;
}

function decodePackedPlot(
  entry: unknown,
  index: number,
  globalMonitorId: string,
  range: [number, number]
): PlotSpec | null {
  if (!Array.isArray(entry) || entry.length < 3) return null;
  const kind = safeInteger(entry[0]) ? PLOT_KIND_BY_TAG[entry[0]] : undefined;
  const parameterId = safeInteger(entry[1]) ? PARAMETER_ID_BY_TAG[entry[1]] : undefined;
  const flags = safeInteger(entry[2]) && entry[2] >= 0 ? entry[2] : null;
  if (!kind || !parameterId || flags == null) return null;

  const extras = entry.length > 3 && safeInteger(entry[3]) && entry[3] >= 0 ? entry[3] : 0;
  let next = 4;
  let location: PlotSpec['location'] = { mode: 'global' };
  let rangeOverride: [number, number] | undefined;
  let cursorOverride: number | undefined;
  let selectedEvent: PlotSpec['selectedEvent'];
  let hiddenEventClassifications: string[] | undefined;

  if (hasBit(extras, PLOT_EXTRA.location)) {
    const monitorId = monitorFromTag(entry[next], globalMonitorId);
    next += 1;
    if (monitorId) location = { mode: 'override', monitorId };
  }
  if (hasBit(extras, PLOT_EXTRA.range)) {
    const value = entry[next];
    next += 1;
    if (Array.isArray(value) && value.length === 2 && safeInteger(value[1]) && value[1] > 0) {
      const from = offsetValue(range[0], value[0]);
      const to = from == null ? null : offsetValue(from, value[1]);
      if (from != null && to != null) rangeOverride = normalizedRange(from, to, range);
    }
  }
  if (hasBit(extras, PLOT_EXTRA.cursor)) {
    const cursor = offsetValue(range[0], entry[next]);
    next += 1;
    if (cursor != null) cursorOverride = clampTimestamp(cursor, rangeOverride ?? range);
  }
  if (hasBit(extras, PLOT_EXTRA.selectedEvent)) {
    const value = entry[next];
    next += 1;
    if (Array.isArray(value) && value.length === 2) {
      const monitorId = monitorFromTag(value[0], globalMonitorId);
      const timestamp = offsetValue(range[0], value[1]);
      if (monitorId && timestamp != null && timestamp >= 0) selectedEvent = { monitorId, timestamp };
    }
  }
  if (hasBit(extras, PLOT_EXTRA.hiddenClassifications)) {
    const value = entry[next];
    next += 1;
    if (Array.isArray(value)) {
      hiddenEventClassifications = value
        .map(classificationFromTag)
        .filter((key): key is string => key != null);
    }
  }

  return normalizePlotSpec({
    id: `url-${index + 1}`,
    kind,
    parameterId,
    location,
    linkedTime: !hasBit(flags, PLOT_FLAG.unlinkedTime),
    ...(rangeOverride ? { rangeOverride } : {}),
    ...(cursorOverride != null ? { cursorOverride } : {}),
    ...(selectedEvent ? { selectedEvent } : {}),
    ...(hiddenEventClassifications?.length ? { hiddenEventClassifications } : {}),
    options: {
      showEvents: hasBit(flags, PLOT_FLAG.showEvents),
      showHistogram: hasBit(flags, PLOT_FLAG.showHistogram),
      cumulative: hasBit(flags, PLOT_FLAG.cumulative),
      showNeutral: hasBit(flags, PLOT_FLAG.showNeutral),
      showFundamental: !hasBit(flags, PLOT_FLAG.hideFundamental),
      perUnit: hasBit(flags, PLOT_FLAG.perUnit),
      showInterharmonics: hasBit(flags, PLOT_FLAG.showInterharmonics)
    }
  });
}

function decodePackedWorkspace(value: string): WorkspaceState | null {
  try {
    const decoded = JSON.parse(decodeBase64Url(value)) as unknown;
    if (!Array.isArray(decoded) || decoded.length < 5 || typeof decoded[0] !== 'string') return null;
    if (!safeInteger(decoded[1]) || !safeInteger(decoded[2]) || decoded[2] <= 0 || !safeInteger(decoded[3]) || !Array.isArray(decoded[4]) || decoded[4].length > MAX_WORKSPACE_PLOTS) return null;
    const end = decoded[1] + decoded[2];
    if (!Number.isSafeInteger(end)) return null;
    const range = normalizedRange(decoded[1], end, FALLBACK_RANGE);
    const cursor = offsetValue(range[0], decoded[3]);
    const plots = decoded[4].flatMap((entry, index) => {
      const plot = decodePackedPlot(entry, index, decoded[0], range);
      return plot ? [plot] : [];
    });
    return {
      version: 2,
      globalMonitorId: decoded[0],
      range,
      cursor: clampTimestamp(cursor ?? range[0], range),
      plots: decoded[4].length > 0 && plots.length === 0 ? [createPlotSpec()] : plots
    };
  } catch {
    return null;
  }
}

function decodeWorkspace(value: string): WorkspaceState | null {
  if (!value || value.length > MAX_WORKSPACE_TOKEN_LENGTH) return null;
  if (value.startsWith(WORKSPACE_CODEC_PREFIX)) {
    return decodePackedWorkspace(value.slice(WORKSPACE_CODEC_PREFIX.length));
  }
  return decodeLegacyWorkspace(value);
}

export function combinedSearchParams(location: Pick<Location, 'search' | 'hash'>): URLSearchParams {
  const result = new URLSearchParams(location.search);
  const question = location.hash.indexOf('?');
  const hashQuery = question >= 0 ? location.hash.slice(question + 1) : location.hash.replace(/^#\/?/, '');
  const hash = new URLSearchParams(hashQuery);
  for (const [key, value] of hash) {
    if (!result.has(key)) result.set(key, value);
  }
  return result;
}

export interface ParseWorkspaceOptions {
  defaultMonitorId: string;
  defaultRange: [number, number];
  rememberedMonitorId?: string | null;
  validMonitorIds?: Set<string>;
}

function validMonitor(value: string, options: ParseWorkspaceOptions): string {
  if (!value || (options.validMonitorIds && !options.validMonitorIds.has(value))) return options.defaultMonitorId;
  return value;
}

export function parseWorkspace(params: URLSearchParams, options: ParseWorkspaceOptions): WorkspaceState {
  const encoded = params.get('w') ?? params.get('workspace');
  const decoded = encoded ? decodeWorkspace(encoded) : null;
  if (decoded) {
    const monitor = validMonitor(decoded.globalMonitorId, options);
    return {
      ...decoded,
      globalMonitorId: monitor,
      plots: decoded.plots.map((plot) => {
        const location = plot.location.mode === 'override' && options.validMonitorIds && !options.validMonitorIds.has(plot.location.monitorId)
          ? { mode: 'global' as const }
          : plot.location;
        const selectedEvent = plot.selectedEvent && options.validMonitorIds && !options.validMonitorIds.has(plot.selectedEvent.monitorId)
          ? undefined
          : plot.selectedEvent;
        return { ...plot, location, selectedEvent };
      })
    };
  }

  const remembered = options.rememberedMonitorId && (!options.validMonitorIds || options.validMonitorIds.has(options.rememberedMonitorId))
    ? options.rememberedMonitorId
    : options.defaultMonitorId;
  const monitorId = validMonitor(params.get('location') ?? remembered, options);
  const from = parseEpoch(params.get('from'), options.defaultRange[0]);
  const to = parseEpoch(params.get('to'), options.defaultRange[1]);
  const range = normalizedRange(from, to, options.defaultRange);
  const parameter = parameterByLegacyId(Number(params.get('parameterType') ?? 0));
  const plot = plotByLegacyId(Number(params.get('plotType') ?? 0));
  const normalized = normalizePlotSpec(createPlotSpec(plot.kind, parameter.id));

  return {
    version: 2,
    globalMonitorId: monitorId,
    range,
    cursor: clampTimestamp(parseEpoch(params.get('cursor'), range[0]), range),
    plots: [normalized]
  };
}

export function workspaceUrl(
  workspace: WorkspaceState,
  comparisonMode: ComparisonMode,
  current: URL,
  analysis?: AnalysisState
): string {
  const url = new URL(current.toString());
  url.hash = '';
  for (const key of ['w', 'workspace', 'location', 'parameterType', 'plotType', 'from', 'to', 'cursor', 'requests', 'comparisonMode', 'prototype', 'p', 'a', 'analysis']) url.searchParams.delete(key);
  // The fragment stays in the browser, avoiding server request-line limits.
  const fragment = new URLSearchParams({ w: encodeWorkspace(workspace) });
  fragment.set('p', comparisonMode === 'same-location' ? 'g' : 'p');
  const encodedAnalysis = analysis ? encodeAnalysisState(analysis, workspace) : null;
  if (encodedAnalysis) fragment.set('a', encodedAnalysis);
  url.hash = fragment.toString();
  return `${url.pathname}${url.search}${url.hash}`;
}

export function parseComparisonMode(params: URLSearchParams): ComparisonMode {
  const value = params.get('p') ?? params.get('comparisonMode') ?? params.get('prototype');
  if (value === 'g' || value === 'global' || value === 'same-location') return 'same-location';
  if (value === 'p' || value === 'per-plot' || value === 'multiple-locations') return 'multiple-locations';
  // The retired hybrid mode allowed per-plot overrides, so retain them by
  // opening legacy links in the mode where all plot locations are editable.
  if (value === 'h' || value === 'hybrid') return 'multiple-locations';
  return DEFAULT_COMPARISON_MODE;
}
